import _ from 'lodash';
import { DELIMITERS } from '@/main/app.constants';
import { FieldDelimiterEnum, ItemTypeEnum, ValidationModeEnum } from '@/sdk/model/DatafileInputV1';
import { dayFirstDefault, delimiterDefault, isConfigurationLoaded } from '@/services/systemConfiguration.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  APPEND,
  COLUMN_INDEX,
  COLUMN_NAME,
  DATAFILE_ITEM_TYPES,
  DAY_MONTH_FIRST_OPTIONS,
  EMPTY_ROW_IDENTIFIER,
  FILE_OUTPUT_TYPES,
  INTERPOLATION_METHODS,
  SIGNAL_VALUE_COLUMN_OPTIONS,
  VALIDATION_MODES,
} from '@/tools/importDatafile/importDatafile.constants';
import type { DatafileInputV1 } from '@/sdk/model/DatafileInputV1';
import type { BaseToolPersistedState, ToolRehydratePayload } from '@/toolSelection/baseTool.store';

export interface ImportDatafileOption<TValue = string> {
  text: string;
  value: TValue;
}

export interface ImportDatafileDuration {
  value: number;
  units: string;
}

export interface ImportDatafileTimezone {
  displayName: string;
}

export type ImportDatafileInterpolationMethod = ImportDatafileOption<string>;
export type ImportDatafileValidationMode = ImportDatafileOption<ValidationModeEnum>;
export type ImportDatafileItemType = ImportDatafileOption<ItemTypeEnum>;
export type ImportDatafileValueColumnOption = ImportDatafileOption<string>;
export type ImportDatafileFileOutputType = ImportDatafileOption<string>;
export type ImportDatafileDelimiterOption = ImportDatafileOption<FieldDelimiterEnum | string>;
export type ImportDatafileDayFirstOption = ImportDatafileOption<boolean>;

export interface ImportDatafileState extends BaseToolPersistedState {
  uploadFilename: string;
  filename: string;
  keyColumnIndex: number;
  keyColumnName: string;
  interpolationMethod?: ImportDatafileInterpolationMethod;
  validationMode?: ImportDatafileValidationMode;
  fileOutputType?: ImportDatafileFileOutputType;
  fieldDelimiter?: ImportDatafileDelimiterOption;
  maxInterpolation: ImportDatafileDuration;
  namePrefix: string;
  nameSuffix: string;
  firstDataRow: number;
  maxInterpolationRow?: number;
  interpolationMethodRow?: number;
  valueUomRow?: number;
  timezone?: ImportDatafileTimezone;
  descriptionRow?: number;
  valueColumnIndices?: string | number;
  valueColumnNames?: string | string[];
  nameRow: number;
  description: string;
  valueColumnOption: ImportDatafileValueColumnOption;
  valueUom: string;
  lenientDaylightSavings: boolean;
  itemType: ImportDatafileItemType;
  maxDuration: ImportDatafileDuration;
  endColumnIndex?: number;
  endColumnName: string;
  conditionName: string;
  dayFirstDefault?: ImportDatafileDayFirstOption;
}

export type ImportDatafileDehydratedState = ImportDatafileState;

export interface ImportDatafileRehydratePayload extends ToolRehydratePayload, ImportDatafileState {
  type: typeof TREND_TOOLS.IMPORTDATAFILE;
}

type ImportDatafileHandlerPayloads = {
  IMPORTDATAFILE_SET_UPLOAD_FILE_NAME: { uploadFilename: string };
  IMPORTDATAFILE_SET_FILE_NAME: { filename: string };
  IMPORTDATAFILE_SET_KEY_COLUMN_INDEX: { keyColumnIndex: number };
  IMPORTDATAFILE_SET_KEY_COLUMN_NAME: { keyColumnName: string };
  IMPORTDATAFILE_SET_INTERPOLATION_METHOD: { interpolationMethod: ImportDatafileInterpolationMethod };
  IMPORTDATAFILE_SET_MAX_INTERPOLATION: { maxInterpolation: ImportDatafileDuration };
  IMPORTDATAFILE_SET_FILE_OUTPUT_TYPE: { fileOutputType: ImportDatafileFileOutputType };
  IMPORTDATAFILE_SET_FIELD_DELIMITER: { fieldDelimiter: ImportDatafileDelimiterOption };
  IMPORTDATAFILE_SET_NAME_PREFIX: { namePrefix: string };
  IMPORTDATAFILE_SET_NAME_SUFFIX: { nameSuffix: string };
  IMPORTDATAFILE_SET_FIRST_DATA_ROW: { firstDataRow: number };
  IMPORTDATAFILE_SET_MAX_INTERPOLATION_ROW: { maxInterpolationRow: number };
  IMPORTDATAFILE_SET_INTERPOLATION_METHOD_ROW: { interpolationMethodRow: number };
  IMPORTDATAFILE_SET_VALUE_UOM_ROW: { valueUomRow: number };
  IMPORTDATAFILE_SET_TIMEZONE: { timezone: ImportDatafileTimezone };
  IMPORTDATAFILE_SET_VALIDATION_MODE: { validationMode: ImportDatafileValidationMode };
  IMPORTDATAFILE_SET_LENIENT_DAYLIGHT_SAVINGS: { lenientDaylightSavings: boolean };
  IMPORTDATAFILE_SET_DESCRIPTION_ROW: { descriptionRow: number };
  IMPORTDATAFILE_SET_VALUE_COLUMN_INDICES: { valueColumnIndices: string };
  IMPORTDATAFILE_SET_VALUE_COLUMN_NAMES: { valueColumnNames: string };
  IMPORTDATAFILE_SET_NAME_ROW: { nameRow: number };
  IMPORTDATAFILE_SET_DESCRIPTION: { description: string };
  IMPORTDATAFILE_SET_VALUE_COLUMN_OPTION: { valueColumnOption: ImportDatafileValueColumnOption };
  IMPORTDATAFILE_SET_VALUE_UOM: { valueUom: string };
  IMPORTDATAFILE_SET_MAX_DURATION: { maxDuration: ImportDatafileDuration };
  IMPORTDATAFILE_SET_END_COLUMN_INDEX: { endColumnIndex: number };
  IMPORTDATAFILE_SET_END_COLUMN_NAME: { endColumnName: string };
  IMPORTDATAFILE_SET_ITEM_TYPE: { itemType: ImportDatafileItemType };
  IMPORTDATAFILE_SET_CONDITION_NAME: { conditionName: string };
  IMPORTDATAFILE_SET_DAY_FIRST_DEFAULT: { dayFirstDefault: ImportDatafileDayFirstOption };
};

type ImportDatafileHandlers = {
  TOOL_REHYDRATE_FOR_EDIT: (payload: ImportDatafileRehydratePayload) => void;
} & { [K in keyof ImportDatafileHandlerPayloads]: (payload: ImportDatafileHandlerPayloads[K]) => void };

export class ImportDatafileStore extends BaseToolStore {
  type = TREND_TOOLS.IMPORTDATAFILE;
  static readonly storeName = 'sqImportDatafileStore';

  initialize(): void {
    const initialState: ImportDatafileState = {
      ...BASE_TOOL_COMMON_PROPS,
      uploadFilename: '',
      filename: '',
      keyColumnIndex: 1,
      keyColumnName: '',
      interpolationMethod: _.find(INTERPOLATION_METHODS, {
        value: 'Linear',
      }) as ImportDatafileInterpolationMethod,
      validationMode: _.find(VALIDATION_MODES, {
        value: ValidationModeEnum.Fail,
      }) as ImportDatafileValidationMode,
      fileOutputType: _.find(FILE_OUTPUT_TYPES, { value: 'Replace' }) as ImportDatafileFileOutputType,
      fieldDelimiter: isConfigurationLoaded()
        ? (DELIMITERS.find((delimiter) => delimiter.value.toLowerCase() === delimiterDefault()) as
            | ImportDatafileDelimiterOption
            | undefined)
        : undefined,
      maxInterpolation: {
        value: 40,
        units: 'h',
      },
      namePrefix: '',
      nameSuffix: '',
      firstDataRow: 2,
      maxInterpolationRow: undefined,
      interpolationMethodRow: undefined,
      valueUomRow: undefined,
      timezone: undefined,
      descriptionRow: undefined,
      valueColumnIndices: undefined,
      valueColumnNames: undefined,
      nameRow: 1,
      description: '',
      valueColumnOption: _.find(SIGNAL_VALUE_COLUMN_OPTIONS, {
        value: 'All',
      }) as ImportDatafileValueColumnOption,
      valueUom: '',
      lenientDaylightSavings: false,
      itemType: _.find(DATAFILE_ITEM_TYPES, {
        value: ItemTypeEnum.Signal,
      }) as ImportDatafileItemType,
      maxDuration: {
        value: 40,
        units: 'h',
      },
      endColumnIndex: undefined,
      endColumnName: '',
      conditionName: '',
      dayFirstDefault: isConfigurationLoaded()
        ? (_.find(DAY_MONTH_FIRST_OPTIONS, {
            value: dayFirstDefault(),
          }) as ImportDatafileDayFirstOption | undefined)
        : undefined,
    };
    this.state = this.immutable(initialState);
  }

  get uploadFilename(): string {
    return this.state.get('uploadFilename');
  }

  get filename(): string {
    return this.state.get('filename');
  }

  get keyColumnIndex(): number {
    return this.state.get('keyColumnIndex');
  }

  get keyColumnName(): string {
    return this.state.get('keyColumnName');
  }

  get interpolationMethod(): ImportDatafileInterpolationMethod | undefined {
    return this.state.get('interpolationMethod');
  }

  get maxInterpolation(): ImportDatafileDuration {
    return this.state.get('maxInterpolation');
  }

  get fileOutputType(): ImportDatafileFileOutputType | undefined {
    return this.state.get('fileOutputType');
  }

  get fieldDelimiter(): ImportDatafileDelimiterOption | undefined {
    return this.state.get('fieldDelimiter');
  }

  get namePrefix(): string {
    return this.state.get('namePrefix');
  }

  get nameSuffix(): string {
    return this.state.get('nameSuffix');
  }

  get firstDataRow(): number {
    return this.state.get('firstDataRow');
  }

  get maxInterpolationRow(): number | undefined {
    return this.state.get('maxInterpolationRow');
  }

  get interpolationMethodRow(): number | undefined {
    return this.state.get('interpolationMethodRow');
  }

  get valueUomRow(): number | undefined {
    return this.state.get('valueUomRow');
  }

  get timezone(): ImportDatafileTimezone | undefined {
    return this.state.get('timezone');
  }

  get validationMode(): ImportDatafileValidationMode | undefined {
    return this.state.get('validationMode');
  }

  get lenientDaylightSavings(): boolean {
    return this.state.get('lenientDaylightSavings');
  }

  get descriptionRow(): number | undefined {
    return this.state.get('descriptionRow');
  }

  get valueColumnIndices(): string | number | undefined {
    return this.state.get('valueColumnIndices');
  }

  get valueColumnNames(): string | string[] | undefined {
    return this.state.get('valueColumnNames');
  }

  get nameRow(): number {
    return this.state.get('nameRow');
  }

  get description(): string {
    return this.state.get('description');
  }

  get valueColumnOption(): ImportDatafileValueColumnOption {
    return this.state.get('valueColumnOption');
  }

  get valueUom(): string {
    return this.state.get('valueUom');
  }

  get itemType(): ImportDatafileItemType {
    return this.state.get('itemType');
  }

  get maxDuration(): ImportDatafileDuration {
    return this.state.get('maxDuration');
  }

  get endColumnName(): string {
    return this.state.get('endColumnName');
  }

  get endColumnIndex(): number | undefined {
    return this.state.get('endColumnIndex');
  }

  get conditionName(): string {
    return this.state.get('conditionName');
  }

  get dayFirstDefault(): ImportDatafileDayFirstOption | undefined {
    return this.state.get('dayFirstDefault');
  }

  /**
   * Returns true if signal is selected
   */
  isSignalSelected(): boolean {
    return (this.state.get('itemType') as ImportDatafileItemType).value === ItemTypeEnum.Signal;
  }

  /**
   * Return true if condition is selected
   */
  isConditionSelected(): boolean {
    return (this.state.get('itemType') as ImportDatafileItemType).value === ItemTypeEnum.Condition;
  }

  /**
   * Returns the store as a datafile api input object
   *
   * @returns The store as an api input object
   */
  asApiObject(): DatafileInputV1 {
    const itemType = this.state.get('itemType') as ImportDatafileItemType;
    const valueColumnOption = this.state.get('valueColumnOption') as ImportDatafileValueColumnOption;
    const maxInterpolation = this.state.get('maxInterpolation') as ImportDatafileDuration;
    const maxDuration = this.state.get('maxDuration') as ImportDatafileDuration;
    const fieldDelimiter = this.state.get('fieldDelimiter') as ImportDatafileDelimiterOption | undefined;
    const interpolationMethod = this.state.get('interpolationMethod') as ImportDatafileInterpolationMethod | undefined;
    const timezone = this.state.get('timezone') as ImportDatafileTimezone | undefined;
    const validationMode = this.state.get('validationMode') as ImportDatafileValidationMode | undefined;
    const fileOutputType = this.state.get('fileOutputType') as ImportDatafileFileOutputType | undefined;

    let valueColumnIndices: string | number | undefined;
    if (itemType.value === ItemTypeEnum.Condition && _.isEqual(valueColumnOption.value, 'None')) {
      valueColumnIndices = 0;
    } else if (_.isEqual(valueColumnOption, COLUMN_INDEX)) {
      valueColumnIndices = this.state.get('valueColumnIndices') as string | undefined;
    } else {
      valueColumnIndices = undefined;
    }

    const storedValueColumnNames = this.state.get('valueColumnNames') as string | string[] | undefined;

    return {
      uploadFilename: this.state.get('uploadFilename'),
      filename: this.state.get('filename'),
      description: this.state.get('description'),
      name: this.state.get('name'),
      fieldDelimiter: (fieldDelimiter?.value ?? FieldDelimiterEnum.Comma) as FieldDelimiterEnum,
      append: fileOutputType?.value === APPEND,
      firstDataRow: this.state.get('firstDataRow'),
      interpolationMethod: interpolationMethod?.value ?? '',
      interpolationMethodRow: this.state.get('interpolationMethodRow')
        ? this.state.get('interpolationMethodRow')
        : EMPTY_ROW_IDENTIFIER,
      keyColumnIndex: this.state.get('keyColumnIndex'),
      keyColumnName: this.state.get('keyColumnName'),
      maximumInterpolation: `${maxInterpolation.value}${maxInterpolation.units}`,
      maximumInterpolationRow: this.state.get('maxInterpolationRow')
        ? this.state.get('maxInterpolationRow')
        : EMPTY_ROW_IDENTIFIER,
      namePrefix: this.state.get('namePrefix'),
      nameSuffix: this.state.get('nameSuffix'),
      nameRow: this.state.get('nameRow'),
      timeZone: timezone?.displayName,
      valueUomRow: this.state.get('valueUomRow') ? this.state.get('valueUomRow') : EMPTY_ROW_IDENTIFIER,
      validationMode: validationMode?.value ?? ValidationModeEnum.Fail,
      lenientDaylightSavings: this.state.get('lenientDaylightSavings'),
      descriptionRow: this.state.get('descriptionRow') ? this.state.get('descriptionRow') : EMPTY_ROW_IDENTIFIER,
      valueColumnIndices: valueColumnIndices as unknown as DatafileInputV1['valueColumnIndices'],
      valueColumnNames: _.isEqual(valueColumnOption, COLUMN_NAME)
        ? (((Array.isArray(storedValueColumnNames) ? storedValueColumnNames.join(',') : storedValueColumnNames) ??
            undefined) as unknown as DatafileInputV1['valueColumnNames'])
        : undefined,
      valueUom: this.state.get('valueUom'),
      endColumnIndex: this.state.get('endColumnIndex'),
      endColumnName: this.state.get('endColumnName'),
      maximumDuration: `${maxDuration.value}${maxDuration.units}`,
      itemType: itemType.value,
      conditionName:
        itemType.value === ItemTypeEnum.Condition
          ? ((this.state.get('conditionName') as string | undefined) ?? undefined)
          : undefined,
      dayFirstDefault: (this.state.get('dayFirstDefault') as ImportDatafileOption<boolean>).value,
    };
  }

  /**
   * Exports the state that is not ephemeral (such as progress indicators).
   * @returns {Object} The dehydrated state.
   */
  dehydrate(): ImportDatafileDehydratedState {
    return this.state.serialize() as ImportDatafileState;
  }

  /**
   * Re-creates the profile search results.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<ImportDatafileDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  rehydrateForEdit(payload: ToolRehydratePayload): void {
    if (payload.type !== TREND_TOOLS.IMPORTDATAFILE) {
      return;
    }

    const {
      type: _type,
      parameters: _parameters,
      formula: _formula,
      ...statePayload
    } = payload as ImportDatafileRehydratePayload;

    this.state.merge(statePayload);
  }

  protected readonly handlers: BaseToolStore['handlers'] & ImportDatafileHandlers = {
    ...this.baseHandlers,
    /**
     * Sets the store fields on tool load. Overrides base rehydrateForEdit
     *
     * @param {Object} payload - Object container for entire store
     */
    TOOL_REHYDRATE_FOR_EDIT: this.rehydrateForEdit,
    /**
     * Sets the name of the file that the server returned after uploading it.
     *
     * @param {Object} payload - Object container
     * @param {String} payload.uploadFilename - The name of the uploaded file
     */
    IMPORTDATAFILE_SET_UPLOAD_FILE_NAME: (payload: { uploadFilename: string }) => {
      this.state.set('uploadFilename', payload.uploadFilename);
    },
    /**
     * Sets the name of the file on the client's computer.
     *
     * @param {Object} payload - Object container
     * @param {String} payload.filename - The name of the file on the client
     */
    IMPORTDATAFILE_SET_FILE_NAME: (payload: { filename: string }) => {
      this.state.set('filename', payload.filename);
    },
    /**
     * Sets the key column index.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.keyColumnIndex - The column index to use for the key
     */
    IMPORTDATAFILE_SET_KEY_COLUMN_INDEX: (payload: { keyColumnIndex: number }) => {
      this.state.set('keyColumnIndex', payload.keyColumnIndex);
    },
    /**
     * Sets the key column name.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.keyColumnName - The column name to use for the key
     */
    IMPORTDATAFILE_SET_KEY_COLUMN_NAME: (payload: { keyColumnName: string }) => {
      this.state.set('keyColumnName', payload.keyColumnName);
    },
    /**
     * Sets the interpolation method (linear or step).
     *
     * @param {Object} payload - Object container
     * @param {Object} payload.interpolationMethod - The interpolation method to use
     */
    IMPORTDATAFILE_SET_INTERPOLATION_METHOD: (payload: { interpolationMethod: ImportDatafileInterpolationMethod }) => {
      this.state.set('interpolationMethod', payload.interpolationMethod);
    },
    /**
     * Sets the maximum interpolation.
     *
     * @param {Object} payload - Object container
     * @param {Object} payload.maxInterpolation - The max interpolations
     */
    IMPORTDATAFILE_SET_MAX_INTERPOLATION: (payload: { maxInterpolation: ImportDatafileDuration }) => {
      this.state.set('maxInterpolation', payload.maxInterpolation);
    },
    /**
     * Sets the file output type (append or replace)
     *
     * @param {Object} payload - Object container
     * @param {Object} payload.fileOutputType - The type used for the file output
     */
    IMPORTDATAFILE_SET_FILE_OUTPUT_TYPE: (payload: { fileOutputType: ImportDatafileFileOutputType }) => {
      this.state.set('fileOutputType', payload.fileOutputType);
    },
    /**
     * Sets the field delimiter for the file
     *
     * @param {Object} payload - Object container
     * @param {String} payload.fieldDelimiter - The character used as the field delimiter
     */
    IMPORTDATAFILE_SET_FIELD_DELIMITER: (payload: { fieldDelimiter: ImportDatafileDelimiterOption }) => {
      this.state.set('fieldDelimiter', payload.fieldDelimiter);
    },
    /**
     * Sets signal name prefix.
     *
     * @param {Object} payload - Object container
     * @param {String} payload.prefix - The prefix for the signal name
     */
    IMPORTDATAFILE_SET_NAME_PREFIX: (payload: { namePrefix: string }) => {
      this.state.set('namePrefix', payload.namePrefix);
    },
    /**
     * Sets the signal name suffix
     *
     * @param {Object} payload - Object container
     * @param {String} payload.nameSuffix - The suffix for the signal name
     */
    IMPORTDATAFILE_SET_NAME_SUFFIX: (payload: { nameSuffix: string }) => {
      this.state.set('nameSuffix', payload.nameSuffix);
    },
    /**
     * Sets the first row with data.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.firstDataRow - The index of the first row with data
     */
    IMPORTDATAFILE_SET_FIRST_DATA_ROW: (payload: { firstDataRow: number }) => {
      this.state.set('firstDataRow', payload.firstDataRow);
    },
    /**
     * Sets the max interpolation row.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.maxInterpolationRow - The index of the max interpolation row
     */
    IMPORTDATAFILE_SET_MAX_INTERPOLATION_ROW: (payload: { maxInterpolationRow: number }) => {
      this.state.set('maxInterpolationRow', payload.maxInterpolationRow);
    },
    /**
     * Sets the interpolation method row
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.interpolationMethodRow - The index of the interpolation method row
     */
    IMPORTDATAFILE_SET_INTERPOLATION_METHOD_ROW: (payload: { interpolationMethodRow: number }) => {
      this.state.set('interpolationMethodRow', payload.interpolationMethodRow);
    },
    /**
     * Sets the uom row.
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.valueUomRow - The index of the uom row
     */
    IMPORTDATAFILE_SET_VALUE_UOM_ROW: (payload: { valueUomRow: number }) => {
      this.state.set('valueUomRow', payload.valueUomRow);
    },
    /**
     * Sets the timezone to use with the timestamps
     *
     * @param {Object} payload - Object container
     * @param {String} payload.timezone- The timezone object
     */
    IMPORTDATAFILE_SET_TIMEZONE: (payload: { timezone: ImportDatafileTimezone }) => {
      this.state.set('timezone', payload.timezone);
    },
    /**
     * Sets how to handle invalid data in the CSV
     *
     * @param {Object} payload - Object container
     * @param {Object} payload.validationMode - Tells which way to handle invalid data
     */
    IMPORTDATAFILE_SET_VALIDATION_MODE: (payload: { validationMode: ImportDatafileValidationMode }) => {
      this.state.set('validationMode', payload.validationMode);
    },
    /**
     * Sets whether daylight savings time is treated leniently or not
     *
     * @param {Object} payload - Object container
     * @param {boolean} payload.lenientDaylightSavings - True to treat as lenient, false otherwise
     */
    IMPORTDATAFILE_SET_LENIENT_DAYLIGHT_SAVINGS: (payload: { lenientDaylightSavings: boolean }) => {
      this.state.set('lenientDaylightSavings', payload.lenientDaylightSavings);
    },
    /**
     * Sets the index of the description row
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.descriptionRow - The index of the description row
     */
    IMPORTDATAFILE_SET_DESCRIPTION_ROW: (payload: { descriptionRow: number }) => {
      this.state.set('descriptionRow', payload.descriptionRow);
    },
    /**
     * Sets the column indices which contain signal values
     *
     * @param {Object} payload - Object container
     * @param {String} payload.valueColumnIndices - The comma-separated column indexes which contain signal values.
     */
    IMPORTDATAFILE_SET_VALUE_COLUMN_INDICES: (payload: { valueColumnIndices: string }) => {
      this.state.set('valueColumnIndices', payload.valueColumnIndices);
    },
    /**
     * Sets the column names which contain signal values
     *
     * @param {Object} payload - Object container
     * @param {String} payload.valueColumnNames - The comma-separated column names which contain signal values.
     */
    IMPORTDATAFILE_SET_VALUE_COLUMN_NAMES: (payload: { valueColumnNames: string }) => {
      this.state.set('valueColumnNames', payload.valueColumnNames);
    },
    /**
     * Sets the name row
     *
     * @param {Object} payload - Object container
     * @param {Number} payload.nameRow - The index of the name row
     */
    IMPORTDATAFILE_SET_NAME_ROW: (payload: { nameRow: number }) => {
      this.state.set('nameRow', payload.nameRow);
    },
    /**
     * Sets the description for the datafile
     *
     * @param {Object} payload - Object container
     * @param {string} payload.description - description of the datafile
     */
    IMPORTDATAFILE_SET_DESCRIPTION: (payload: { description: string }) => {
      this.state.set('description', payload.description);
    },
    /**
     * Sets the data column option
     *
     * @param {Object} payload - Object container
     * @param {boolean} payload.valueColumnOption- data column option
     */
    IMPORTDATAFILE_SET_VALUE_COLUMN_OPTION: (payload: { valueColumnOption: ImportDatafileValueColumnOption }) => {
      this.state.set('valueColumnOption', payload.valueColumnOption);
    },
    /**
     * Sets the uom for the whole datafile
     *
     * @param {Object} payload- Object container
     * @param {String} payload.valueUom - the uom
     */
    IMPORTDATAFILE_SET_VALUE_UOM: (payload: { valueUom: string }) => {
      this.state.set('valueUom', payload.valueUom);
    },
    /**
     * Sets the maximum duration
     *
     * @param {Object} payload- Object container
     * @param {Object} payload.maxDuration - the maximum duration
     */
    IMPORTDATAFILE_SET_MAX_DURATION: (payload: { maxDuration: ImportDatafileDuration }) => {
      this.state.set('maxDuration', payload.maxDuration);
    },
    /**
     * Sets the end column index
     *
     * @param {Object} payload- Object container
     * @param {number} payload.endColumnIndex - the end column index
     */
    IMPORTDATAFILE_SET_END_COLUMN_INDEX: (payload: { endColumnIndex: number }) => {
      this.state.set('endColumnIndex', payload.endColumnIndex);
    },
    /**
     * Sets the end column name
     *
     * @param {Object} payload- Object container
     * @param {String} payload.endColumnName - the end column name
     */
    IMPORTDATAFILE_SET_END_COLUMN_NAME: (payload: { endColumnName: string }) => {
      this.state.set('endColumnName', payload.endColumnName);
    },
    /**
     * Sets the item type
     *
     * @param {Object} payload- Object container
     * @param {Object} payload.itemType - the item type
     */
    IMPORTDATAFILE_SET_ITEM_TYPE: (payload: { itemType: ImportDatafileItemType }) => {
      this.state.set('itemType', payload.itemType);
      const currentValueOption = this.state.get('valueColumnOption') as ImportDatafileValueColumnOption;
      if (payload.itemType.value === ItemTypeEnum.Signal && currentValueOption.value === 'None') {
        this.state.set('valueColumnOption', _.find(SIGNAL_VALUE_COLUMN_OPTIONS, { value: 'All' }));
      }
      if (payload.itemType.value === ItemTypeEnum.Condition) {
        this.state.set('valueColumnOption', COLUMN_NAME);
      }
    },
    /**
     * Sets the condition name
     *
     * @param {Object} payload- Object container
     * @param {String} payload.conditionName - the name of the condition
     */
    IMPORTDATAFILE_SET_CONDITION_NAME: (payload: { conditionName: string }) => {
      this.state.set('conditionName', payload.conditionName);
    },
    /**
     *Sets whether day is first or month is first in MDY/DMY styles dates
     *
     * @param {Object} payload - Object container
     * @param {Object} payload.dayFirstDefault - One of DAY_MONTH_FIRST_OPTIONS
     */
    IMPORTDATAFILE_SET_DAY_FIRST_DEFAULT: (payload: { dayFirstDefault: ImportDatafileOption<boolean> }) => {
      this.state.set('dayFirstDefault', payload.dayFirstDefault);
    },
  };
}
